import { Backdrop, Box, CircularProgress } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import { RootState } from "../../../store/store";
import basestyle from "../../CSS/Base.module.css";
import { default as CancelPopUp, default as PublishPopUp } from "../../PopUp/PopUp";
import { copychartAccountDetailsByYear, getChartAccountDetailsByYear, publishChartAccountDetails, replaceChartAccountDetails } from "../services/Financials/budget.service";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer, SubSectionTitle } from "../StyledComponents/Container";
import { notifyUser } from "../Utils/notifyUser";
import ReusableChartsAccountTable, { Column } from "./ReusableChartsAccountTable";

const BudgetAccountDetails = () => {
    const { state } = useLocation();
    const [chartAssetAccountsList, setChartAssetAccountsList] = useState<any[]>([]);
    const [chartNetAssetAccountsList, setChartNetAssetAccountsList] = useState<any[]>([]);
    const [chartIncomeAccountsList, setChartIncomeAccountsList] = useState<any[]>([]);
    const [chartExpensesAccountsList, setChartExpensesAccountsList] = useState<any[]>([]);
    const [chartLiabilitiesAccountsList, setChartLiabilitiesAccountsList] = useState<any[]>([]);
    const [totalIncome, setTotalIncome] = useState(0);
    const [totalExpenses, settotalExpenses] = useState(0);
    const [totalNetAsset, settotalNetAsset] = useState(0);
    const [totalAsset, settotalAasset] = useState(0);
    const [totalLiabilities, settotalLiabilities] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [toRefresh, setToRefresh] = useState(false);
    const [loadingFlag, setLoadingFlag] = useState(false);
    const [expandAll, setExpandAll] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [method] = useState(state?.method ?? "");
    const [copychartAccountResponse] = useState(state?.copychartAccountResponse ?? "");
    const budgetYear = state?.budgetYear ?? "";
    const type = state?.type ?? 0;
    const copyFrom = state?.copyFrom ?? 0;
    const [publishOpenPopUp, setPublishOpenPopUp] = useState(false);
    const [openPopUp, setOpenPopUp] = useState(false);
    const navigate = useNavigate();
    const userDetails = useSelector((state: RootState) => state.auth);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const [saveUpdatedChartAccountsList, setSaveUpdatedChartAccountsList] = useState<any[]>([]);

    const columns: Column[] = [
        { id: "accountNumber", label: translate('chartAccount'), minWidth: 150, align: "center" },
        { id: "accountName", label: translate('description'), minWidth: 150, align: "left" },
        { id: "editDelete", label: translate('editremove'), minWidth: 150, align: "center" },
    ];


    useEffect(() => {
        if (toRefresh) {
            getChartAccountDetailsByYearInfo();
            setExpandAll(false);
        }
    }, [toRefresh]);

    // Helper method to reset chart account lists
    function resetChartAccountLists() {
        setChartIncomeAccountsList([]);
        setChartAssetAccountsList([]);
        setChartLiabilitiesAccountsList([]);
        setChartNetAssetAccountsList([]);
        setChartExpensesAccountsList([]);
    }
    useEffect(() => {
        if (method === "Reopen") {
            getChartAccountDetailsByYearInfo();
        } else if (method === "Replace") {
            replaceChartAccountDetailsInfo();
        } else {
            if (!copychartAccountResponse) {
                getChartAccountsInfo();
            } else {
                if (copychartAccountResponse?.length > 0) {
                    processChartAccounts(copychartAccountResponse);
                } else {
                    resetChartAccountLists();
                }

            }
        }
    }, [method, copychartAccountResponse]);

    async function getChartAccountDetailsByYearInfo() {
        setIsLoading(true);
        try {
            const responseList = await getChartAccountDetailsByYear(budgetYear, type, userDetails.clientId);
            await handleChartAccountResponse(responseList);
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
            setTimeout(() => {
                setRefreshKey(prev => prev + 1);
                setToRefresh(false);
            }, 100);
        }
    }
    async function replaceChartAccountDetailsInfo() {
        setIsLoading(true);
        try {
            const responseList = await replaceChartAccountDetails(copyFrom, budgetYear, type);
            await handleChartAccountResponse(responseList);
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    }

    async function getChartAccountsInfo() {
        setIsLoading(true);
        try {
            const responseList = await copychartAccountDetailsByYear(copyFrom, budgetYear, type);
            await handleChartAccountResponse(responseList);
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    }
    function processChartAccounts(chartAccountsVOList: any[]) {
        const assetAccounts = chartAccountsVOList.filter((asset: { accountType: string; }) => asset.accountType === "asset");
        const liabilityAccounts = chartAccountsVOList.filter((liabilities: { accountType: string; }) => liabilities.accountType === "liabilities");
        const netAssetAccounts = chartAccountsVOList.filter((net: { accountType: string; }) => net.accountType === "net_assets");
        const incomeAccounts = chartAccountsVOList.filter((income: { accountType: string; }) => income.accountType === "income");
        const expensesAccounts = chartAccountsVOList.filter((expenses: { accountType: string; }) => expenses.accountType === "expenses");

        setChartAssetAccountsList(assetAccounts);
        setChartLiabilitiesAccountsList(liabilityAccounts);
        setChartIncomeAccountsList(incomeAccounts);
        setChartNetAssetAccountsList(netAssetAccounts);
        setChartExpensesAccountsList(expensesAccounts);

        // Reset totals before summing
        setTotalIncome(0);
        settotalLiabilities(0);
        settotalNetAsset(0);
        settotalAasset(0);
        settotalExpenses(0);

        chartAccountsVOList.forEach((account: any) => {
            switch (account.accountType) {
                case "income":
                    setTotalIncome(prev => prev + account.curActualAmount);
                    break;
                case "liabilities":
                    settotalLiabilities(prev => prev + account.curActualAmount);
                    break;
                case "net_assets":
                    settotalNetAsset(prev => prev + account.curActualAmount);
                    break;
                case "asset":
                    settotalAasset(prev => prev + account.curActualAmount);
                    break;
                case "expenses":
                    settotalExpenses(prev => prev + account.curActualAmount);
                    break;
            }
        });
    }
    // Helper method for processing chart account data
    async function handleChartAccountResponse(responseList: any) {
        if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
            const chartAccountsVOList = responseList?.data?.data?.masterChartAccountsVOList;
            if (chartAccountsVOList?.length > 0) {
                if (chartAccountsVOList?.length > 0) {
                    processChartAccounts(chartAccountsVOList);
                } else {
                    resetChartAccountLists();
                }
            } else {
                resetChartAccountLists();
                notifyUser(responseList?.data?.message, 'error', translate('budgetAccountDetails'));
            }
        }

    }

    // Helper method to handle errors
    function handleError(error: any) {
        if (error instanceof AxiosError) {
            console.error(translate('axiosError'), error.response?.data ?? error.message);
        } else {
            console.error(translate('unexpectedError'), error);
        }
    }


    const handleCancel = () => {
        setOpenPopUp(true);
    };
    const handlePopUpClose = () => {
        setOpenPopUp(false);
        setPublishOpenPopUp(false);
    };
    function handleConfirm() {
        navigate("/budgetListing");
    }
    const handlePublish = () => {
        setPublishOpenPopUp(true);
    };
    const handleCollapseAll = () => {
        setLoadingFlag(true);
        setTimeout(() => {
            setExpandAll(prev => !prev);
            setLoadingFlag(false);
        }, 3000);
    };
    async function handlePublishConfirm() {
        setPublishOpenPopUp(false);
        setLoadingFlag(true);
        try {
            const responseList = await publishChartAccountDetails(budgetYear, type, userDetails.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                navigate("/budgetListing");
                setLoadingFlag(false);
                notifyUser(responseList?.data?.message, 'success', translate('budgetAccountDetails'));
            } else {
                setLoadingFlag(false);
                notifyUser(responseList?.data?.message, 'error', translate('budgetAccountDetails'));
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                notifyUser(error.message ?? error.response?.data, 'error', translate('budgetAccountDetails'));
                console.error(translate('axiosError'), error.message ?? error.response?.data);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {

            setLoadingFlag(false);
        }

    }


    const handleFinalDataUpdate = (finalUpdatedData: any[]) => {
        setSaveUpdatedChartAccountsList(finalUpdatedData)
    };

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('accountDetails')} </SectionTitle>
                    <Box sx={{ mx: 2 }}>
                        <Box className={basestyle.buttoncontainer} sx={{ mt: -1 }}>
                            <ContainedButton onClick={handleCollapseAll}> {expandAll ? translate('collapseAll') : translate('expandAll')}</ContainedButton>
                        </Box>

                        <SubSectionTitle  >{translate('assetDetails')}</SubSectionTitle  >

                        <ReusableChartsAccountTable
                            tableKey="BudgetAccountDetails"
                            columns={columns}
                            rows={chartAssetAccountsList}
                            loading={isLoading}
                            forecastYear={budgetYear}
                            totalAmount={totalAsset}
                            isExpand={expandAll}
                            pageType="BudgetAccountDetails"
                            type={"asset"}
                            typeId={type}
                            onDataChange={handleFinalDataUpdate}
                            setToRefresh={setToRefresh}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('liabilitiesDetails')}</SubSectionTitle  >

                        <ReusableChartsAccountTable
                            tableKey="BudgetAccountDetails"
                            columns={columns}
                            rows={chartLiabilitiesAccountsList}
                            loading={isLoading}
                            forecastYear={budgetYear}
                            totalAmount={totalLiabilities}
                            isExpand={expandAll}
                            pageType="BudgetAccountDetails"
                            type={"liabilities"}
                            typeId={type}
                            onDataChange={handleFinalDataUpdate}
                            setToRefresh={setToRefresh}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('netAssetsDetails')}</SubSectionTitle  >

                        <ReusableChartsAccountTable
                            tableKey="BudgetAccountDetails"
                            columns={columns}
                            rows={chartNetAssetAccountsList}
                            loading={isLoading}
                            forecastYear={budgetYear}
                            totalAmount={totalNetAsset}
                            isExpand={expandAll}
                            pageType="BudgetAccountDetails"
                            type={"net_assets"}
                            typeId={type}
                            onDataChange={handleFinalDataUpdate}
                            setToRefresh={setToRefresh}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('incomeDetails')}</SubSectionTitle  >

                        <ReusableChartsAccountTable
                            tableKey="BudgetAccountDetails"
                            columns={columns}
                            rows={chartIncomeAccountsList}
                            loading={isLoading}
                            forecastYear={budgetYear}
                            totalAmount={totalIncome}
                            isExpand={expandAll}
                            pageType="BudgetAccountDetails"
                            type={"income"}
                            typeId={type}
                            onDataChange={handleFinalDataUpdate}
                            setToRefresh={setToRefresh}
                            refreshKey={refreshKey}
                        />

                        <SubSectionTitle  >{translate('expensesDetails')}</SubSectionTitle  >

                        <ReusableChartsAccountTable
                            tableKey="BudgetAccountDetails"
                            columns={columns}
                            rows={chartExpensesAccountsList}
                            loading={isLoading}
                            forecastYear={budgetYear}
                            totalAmount={totalExpenses}
                            isExpand={expandAll}
                            pageType="BudgetAccountDetails"
                            type={"expenses"}
                            typeId={type}
                            onDataChange={handleFinalDataUpdate}
                            setToRefresh={setToRefresh}
                            refreshKey={refreshKey}
                        />

                        <Box className={basestyle.buttoncontainer} >
                            <ContainedButton onClick={handlePublish}> {translate('publish')}  </ContainedButton>
                            <OutlinedButton onClick={handleCancel}  >{translate('cancel')}   </OutlinedButton>
                        </Box>
                    </Box>
                    <PublishPopUp
                        open={publishOpenPopUp}
                        popupTitle={translate('publishConformation')}
                        handleClose={handlePopUpClose}
                        saveChanges={() => handlePublishConfirm()}
                        message={translate('doYouWantToPublishForAllParticipants?')}
                    />
                    <CancelPopUp
                        open={openPopUp}
                        popupTitle={translate('confirmCancel')}
                        handleClose={handlePopUpClose}
                        saveChanges={() => handleConfirm()}
                        message={translate('doYouWantToCancelChanges?')}
                    />
                    <Backdrop
                        sx={{
                            color: "#fff",
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                            display: "flex",
                            flexDirection: "column",
                        }}
                        open={loadingFlag}
                    >
                        <CircularProgress color="inherit" />
                        <SubSectionTitle  >{translate('pleasewait')}</SubSectionTitle>
                    </Backdrop>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default BudgetAccountDetails;